import { gql, type TypedDocumentNode } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Collection } from '@sorare/core/src/atoms/icons/Collection';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_MY_CARDS_GALLERY } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';

import type { MissionCollectionCard_cardCollection } from './MissionCollectionCard/__generated__/index.graphql.ts';
import { MissionCollectionCard } from './MissionCollectionCard/index.tsx';
import type {
  TaskCollections_currentUser,
  TaskCollections_taskInterface,
} from './__generated__/index.graphql.ts';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(100px, 1fr));
  gap: var(--unit);
`;
type Props = {
  task: TaskCollections_taskInterface;
  currentUser: Nullable<TaskCollections_currentUser>;
};

export const TaskCollections = ({ task, currentUser }: Props) => {
  const { generateSportContextPath } = useSportContext();
  if (!isType(task, 'CompleteCollectionsCountTask')) {
    return null;
  }
  const { cardCollections } = currentUser || {};
  const { nodes } = cardCollections || {};
  return (
    <Vertical>
      <Horizontal>
        <div style={{ color: 'var(--c-set-surface-primary)' }}>
          <Collection size={2} />
        </div>
        <BrandS>
          <FormattedMessage defaultMessage="My collection" />
        </BrandS>
      </Horizontal>
      <Grid>
        {nodes?.map((node: MissionCollectionCard_cardCollection) => (
          <MissionCollectionCard key={node.slug} cardCollection={node} />
        ))}
      </Grid>
      <Button
        size="medium"
        color="quaternary"
        to={generateSportContextPath(ANY_SPORT_MY_CARDS_GALLERY)}
      >
        <FormattedMessage {...glossary.viewMore} />
      </Button>
    </Vertical>
  );
};

TaskCollections.fragments = {
  taskInterface: gql`
    fragment TaskCollections_taskInterface on TaskInterface {
      id
    }
  ` as TypedDocumentNode<TaskCollections_taskInterface>,
  currentUser: gql`
    fragment TaskCollections_currentUser on CurrentUser {
      slug
      cardCollections(
        sport: $sport
        sortType: HIGHEST_SCORE
        first: 6
        cardSetSlug: $cardSetSlug
      ) {
        nodes {
          slug
          ...MissionCollectionCard_cardCollection
        }
      }
    }
    ${MissionCollectionCard.fragments.cardCollection}
  ` as TypedDocumentNode<TaskCollections_currentUser>,
};
